import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import SearchInput from '@/components/SearchInput'
import CategoryFilter from '@/components/CategoryFilter'
import ImageCard from '@/components/ImageCard'
import Loading from '@/components/Loading'
import EmptyState from '@/components/EmptyState'
import { useFilteredImages } from '@/hooks/useImages'

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('q') || '')
  const [category, setCategory] = useState(searchParams.get('category') || 'all')

  const { images, isLoading } = useFilteredImages(category, search)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('q', value)
    } else {
      params.delete('q')
    }
    setSearchParams(params, { replace: true })
  }

  const handleCategoryChange = (cat: string) => {
    setCategory(cat)
    const params = new URLSearchParams(searchParams)
    if (cat !== 'all') {
      params.set('category', cat)
    } else {
      params.delete('category')
    }
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Browse Images</h1>
        <p className="text-dark-400">
          Explore our catalog of CVE-free container images
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-64 flex-shrink-0">
          <div className="lg:sticky lg:top-24 space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-2">
                Search
              </label>
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder="Filter images..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-3">
                Categories
              </label>
              <div className="lg:flex lg:flex-col lg:gap-1">
                <CategoryFilter selected={category} onChange={handleCategoryChange} />
              </div>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          {isLoading ? (
            <Loading text="Loading images..." />
          ) : images.length === 0 ? (
            <EmptyState
              title="No images found"
              description={
                search || category !== 'all'
                  ? 'Try adjusting your filters or search terms'
                  : 'No images are available yet'
              }
            />
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-dark-400">
                  {images.length} image{images.length !== 1 ? 's' : ''} found
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {images.map(image => (
                  <ImageCard key={image.name} image={image} />
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}
